import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';

import {
  Container,
  Content,
  LogoImg,
  ButtonCustom,
  Input,
  SignInLink,
  TextFieldCustom,
} from './CustomerPortalResetPassword.styles';

import logoLoginDefault from '../../../assets/logoLogin.svg';
import IFormError from '../../../interfaces/IFormError';
import { useToastr } from '../../../hooks/useToastr';
import getValidationError from '../../../util/getValidationError';
import IPage from '../../../interfaces/IPage';
import { useCustomerPortalUserPasswordService } from '../../../services/useCustomerPortalUserPasswordService';
import { useOpenedRoutesService } from '../../../services/useOpenedRoutesService';
import { BackdropCustom } from '../../../components/backdrop/Backdrop';

const CustomerPortalResetPassword: React.FC<IPage> = ({ title }) => {
  window.document.title = title;

  const navigate = useNavigate();
  const toastr = useToastr();
  const location = useLocation();

  const customerPortalUserPasswordService = useCustomerPortalUserPasswordService();
  const { getCustomization } = useOpenedRoutesService();

  const [tenant] = useState(() => {
    return process.env.REACT_APP_CLIENT_TENANT_DOMAIN
      ? process.env.REACT_APP_CLIENT_TENANT_DOMAIN
      : location.search.replaceAll('?tenant=', '');
  });
  const [passwordResetToken] = useState(() => {
    return location.pathname.replaceAll('/customer-portal/reset-password/', '');
  });

  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');

  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErros] = useState<IFormError>({});

  const [loadingCustom, setLoadingCustom] = useState(false);
  const [logoLogin, setLogoLogin] = useState<string | null>(null);

  const handleSubmit = useCallback(async (): Promise<void> => {
    setLoading(true);
    setFormErros({});

    try {
      const data = {
        passwordResetToken,
        password,
        passwordConfirmation,
        tenant,
      };

      const schema = Yup.object().shape({
        password: Yup.string().required('Senha obrigatória'),
        passwordConfirmation: Yup.string().required('Confirmação de senha obrigatória'),
      });

      await schema.validate(data, {
        abortEarly: false,
      });

      await customerPortalUserPasswordService
        .resetPassword(data)
        .then(() => {
          toastr.success(`Senha alterada com sucesso`);
          navigate(`/customer-portal/signin?tenant=${tenant}`);
        })
        .catch(error => {
          toastr.error(error.message || 'Ocorreu um erro ao recuperar a senha.');
        });
    } catch (err: Yup.ValidationError | any) {
      if (err instanceof Yup.ValidationError) {
        const errors = getValidationError(err);
        setFormErros(errors);
        return;
      }

      toastr.error('Ocorreu um erro ao alterar senha.');
    } finally {
      setLoading(false);
    }
  }, [passwordResetToken, password, passwordConfirmation, tenant, navigate, toastr]);

  const handleGetCustomization = useCallback(async () => {
    setLoadingCustom(true);
    await getCustomization(btoa(tenant))
      .then(customization => {
        localStorage.setItem('@PEDIDO-WEB:customization', JSON.stringify(customization));

        setLogoLogin(customization.logoLogin);

        if (customization) {
          document.documentElement.style.setProperty('--main-color', `${customization?.mainColor}` || '#088671');
          document.documentElement.style.setProperty('--hover-color', `${customization?.hoverColor}` || '#08816d');
        }
      })
      .catch(error => {
        toastr.error(`${error.message}. Contate o suporte.` || 'Erro ao obter domínio. Contate o suporte.');
      })
      .finally(() => {
        setLoadingCustom(false);
      });
  }, [tenant, toastr]);

  useEffect(() => {
    if (tenant) {
      handleGetCustomization();
    }
  }, [tenant]);

  return (
    <Container>
      {loadingCustom ? (
        <BackdropCustom />
      ) : (
        <Content elevation={5}>
          <LogoImg>
            <img src={logoLogin || logoLoginDefault} alt="Portal de Boletos" />
          </LogoImg>

          <form>
            <Input>
              <div>
                <label>Senha</label>
              </div>
              <TextFieldCustom
                size="small"
                value={password}
                onChange={e => setPassword(e.target.value)}
                type="password"
                autoComplete="off"
                helperText={formErrors.password}
                error={!!formErrors.password}
                id="password"
              />
            </Input>

            <Input>
              <div>
                <label>Confirmar senha</label>
              </div>
              <TextFieldCustom
                size="small"
                value={passwordConfirmation}
                onChange={e => setPasswordConfirmation(e.target.value)}
                type="password"
                autoComplete="off"
                helperText={formErrors.passwordConfirmation}
                error={!!formErrors.passwordConfirmation}
                id="password"
              />
            </Input>

            <ButtonCustom onClick={handleSubmit} loading={loading} variant="contained" id="submit">
              Confirmar
            </ButtonCustom>
          </form>

          <SignInLink>
            <Link to={`/customer-portal/signin?tenant=${tenant}`}>
              <KeyboardBackspaceIcon />
              Voltar para tela de login
            </Link>
          </SignInLink>
        </Content>
      )}
    </Container>
  );
};

export { CustomerPortalResetPassword };
